// partnerAuth.js
// Restaurant-partner session handling. Deliberately plain fetch + localStorage rather
// than Amplify: Amplify.configure() is a global singleton already owned by the GUEST
// Google login (awsConfig.js), so a second identity type can't share it. Login here is
// email/password against a dedicated Cognito app client, brokered by the partnerAuth
// Lambda (the browser never talks to Cognito directly — see partner-portal plan §1).
//
// Shape/reasoning mirrors redspeed-website/lib/session.ts, which is the proven pattern
// in this monorepo for talking to the same shared Cognito pool.

const API_BASE = 'https://z51smv2b7g.execute-api.ap-southeast-2.amazonaws.com/prod';
const KEY = 'gg_partner_session';

export function getSession() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setSession(session) {
  localStorage.setItem(KEY, JSON.stringify(session));
}

export function logout() {
  localStorage.removeItem(KEY);
}

// Display-only. The server never trusts anything decoded here — it re-derives the
// caller's restaurant from the verified JWT + its own DynamoDB lookup on every call.
export function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(decodeURIComponent(escape(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))));
  } catch {
    return {};
  }
}

// The API Gateway authorizer validates `aud` against the app client id, and only the
// ID token carries that claim — same reasoning as redspeed-website/lib/session.ts.
export function bearerToken() {
  const s = getSession();
  return (s && s.tokens && s.tokens.idToken) || '';
}

export function isAuthenticated() {
  const s = getSession();
  if (!s || !s.tokens || !s.tokens.idToken) return false;
  return !s.tokens.expiresAt || Date.now() < s.tokens.expiresAt;
}

function persistTokens(email, data) {
  const claims = decodeJwt(data.idToken);
  setSession({
    email,
    name: claims.name || claims.email || email,
    tokens: {
      idToken: data.idToken,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt: Date.now() + (data.expiresIn || 3600) * 1000,
    },
  });
}

async function post(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// Returns { challenge: 'NEW_PASSWORD_REQUIRED', session } on first login with a
// temporary password; otherwise persists the session and returns { challenge: null }.
export async function login(email, password) {
  const normalized = String(email).trim().toLowerCase();
  const data = await post('/partner/auth/login', { email: normalized, password });
  if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
    return { challenge: data.challengeName, session: data.session, email: normalized };
  }
  persistTokens(normalized, data);
  return { challenge: null };
}

export async function respondToChallenge(email, session, newPassword) {
  const normalized = String(email).trim().toLowerCase();
  const data = await post('/partner/auth/challenge', { email: normalized, session, newPassword });
  persistTokens(normalized, data);
}

export async function refresh() {
  const s = getSession();
  if (!s || !s.tokens || !s.tokens.refreshToken) throw new Error('No refresh token');
  const data = await post('/partner/auth/refresh', { email: s.email, refreshToken: s.tokens.refreshToken });
  persistTokens(s.email, { ...data, refreshToken: data.refreshToken || s.tokens.refreshToken });
}

const partnerAuth = { getSession, logout, decodeJwt, bearerToken, isAuthenticated, login, respondToChallenge, refresh };
export default partnerAuth;
